import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import CopyButton from "@/components/admin/CopyButton";
import {
  Loader2, Send, Check, X, KeyRound, Upload, Sparkles, Film,
  PackageOpen, Download, ListChecks,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";

const API = getApiUrl();
const MASK = "••••••••";

export default function RendyTestingSection() {
  const [settings, setSettings] = useState({ api_key: "", base_url: "" });
  const [apiKeySet, setApiKeySet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [useManual, setUseManual] = useState(false);
  const [manualAddress, setManualAddress] = useState("");
  const [manualImageUrls, setManualImageUrls] = useState("");
  const [ratio, setRatio] = useState("portrait");
  const [presetKey, setPresetKey] = useState("DEFAULT");
  const [cameraActionKey, setCameraActionKey] = useState("");
  const [busyAction, setBusyAction] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [listingId, setListingId] = useState("");
  const [polling, setPolling] = useState(false);
  const [listingDetail, setListingDetail] = useState(null);
  const [jobId, setJobId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, propsResp] = await Promise.all([
          axios.get(`${API}/admin/integrations/rendy-settings`, { withCredentials: true }),
          axios.get(`${API}/properties`),
        ]);
        setSettings({ api_key: data.api_key || "", base_url: data.base_url || "" });
        setApiKeySet(!!data.api_key_set);
        setProperties(propsResp.data || []);
        if ((propsResp.data || []).length) setPropertyId(propsResp.data[0].property_id);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Rendy settings");
      } finally {
        setLoading(false);
      }
    })();
    return () => clearInterval(pollRef.current);
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const body = { ...settings };
      if (body.api_key === MASK) delete body.api_key;
      await axios.put(`${API}/admin/integrations/rendy-settings`, body, { withCredentials: true });
      toast.success("Saved Rendy settings");
      setApiKeySet(!!(body.api_key || apiKeySet));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const call = async (body) => {
    const { data } = await axios.post(`${API}/admin/integrations/rendy-test-call`, body, { withCredentials: true });
    setLastResult(data);
    return data;
  };

  const runEndpoint = async (action) => {
    setBusyAction(action);
    setLastResult(null);
    try {
      await call({ action });
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  const buildCreatePayload = (dry_run) => ({
    action: "create_listing",
    property_id: useManual ? undefined : propertyId || undefined,
    manual_address: useManual ? manualAddress : undefined,
    manual_image_urls: useManual
      ? manualImageUrls.split("\n").map((s) => s.trim()).filter(Boolean)
      : undefined,
    ratio,
    preset_key: presetKey,
    camera_action_key: cameraActionKey || undefined,
    dry_run,
  });

  const previewPayload = async () => {
    setBusyAction("preview");
    setLastResult(null);
    try {
      await call(buildCreatePayload(true));
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  const generateVideo = async () => {
    setBusyAction("generate");
    setLastResult(null);
    setListingDetail(null);
    try {
      const data = await call(buildCreatePayload(false));
      const newListingId = data?.body?.listingId;
      if (newListingId) {
        setListingId(newListingId);
        toast.success(`Listing started: ${newListingId}`);
        startPolling(newListingId);
      } else {
        toast.error(data?.body?.error || data?.body?.message || data?.detail || "Rendy did not return a listingId");
      }
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  const startPolling = (id) => {
    clearInterval(pollRef.current);
    setPolling(true);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const data = await call({ action: "listing_status", listing_id: id });
        const body = data?.body || {};
        if (body.status === "success" && body.textHooks !== "pending") {
          clearInterval(pollRef.current);
          setPolling(false);
          fetchDetail(id);
        } else if (body.status === "error" || attempts >= 40) {
          clearInterval(pollRef.current);
          setPolling(false);
        }
      } catch {
        clearInterval(pollRef.current);
        setPolling(false);
      }
    }, 4000);
  };

  const fetchDetail = async (id) => {
    setBusyAction("detail");
    try {
      const data = await call({ action: "listing_detail", listing_id: id || listingId });
      setListingDetail(data?.body || null);
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  const startExport = async () => {
    setBusyAction("export");
    try {
      const data = await call({ action: "export", listing_id: listingId });
      const newJobId = data?.body?.jobId;
      if (newJobId) {
        setJobId(newJobId);
        toast.success(`Export started: ${newJobId}`);
      } else if (data?.body?.downloadUrl) {
        toast.success("Cached export ready — see JSON below for downloadUrl");
      } else {
        toast.error(data?.body?.error || data?.body?.message || data?.detail || "Export did not return a jobId");
      }
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  const checkExport = async () => {
    setBusyAction("export_status");
    try {
      await call({ action: "export_status", job_id: jobId });
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  const uploadImage = async () => {
    if (!uploadFile) {
      toast.error("Choose an image file first");
      return;
    }
    setBusyAction("upload");
    setLastResult(null);
    try {
      const form = new FormData();
      form.append("image", uploadFile);
      const { data } = await axios.post(`${API}/admin/integrations/rendy-upload-image`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Rendy testing…
      </div>
    );
  }

  const runDurationBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${API}/admin/rendy/backfill-video-durations`, {}, { withCredentials: true });
      toast.success(
        data.scanned > 0
          ? `Stored durations for ${data.updated} of ${data.scanned} gallery video(s)${data.failed ? ` (${data.failed} could not be read)` : ""}.`
          : "Every gallery video already has a stored duration."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duration backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const runCleanup = async () => {
    if (!confirm(
      "This scans every property's gallery and removes exact duplicate video/image URLs " +
      "(keeping the earliest copy). This cannot be undone. Continue?"
    )) return;
    setCleaningUp(true);
    try {
      const { data } = await axios.post(`${API}/admin/rendy/cleanup-duplicate-gallery`, {}, { withCredentials: true });
      toast.success(
        data.items_removed > 0
          ? `Removed ${data.items_removed} duplicate item(s) across ${data.properties_fixed} property/properties (scanned ${data.properties_scanned}).`
          : `No duplicates found (scanned ${data.properties_scanned} properties).`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rendy-testing-section">
      <IntegrationApiLogToggle provider="rendy" />

      {/* Gallery cleanup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageOpen className="h-5 w-5 text-primary" /> Gallery Cleanup
          </CardTitle>
          <CardDescription>
            Sweeps every property and removes exact duplicate gallery entries (same media_url added more than once) —
            useful after the Rendy add/remove-from-gallery race condition that some properties accumulated from.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={runCleanup}
            disabled={cleaningUp}
            data-testid="rendy-cleanup-duplicates-btn"
          >
            {cleaningUp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackageOpen className="h-4 w-4 mr-2" />}
            Clean up duplicate gallery videos
          </Button>
          <Button
            variant="outline"
            onClick={runDurationBackfill}
            disabled={backfilling}
            className="ml-2"
            data-testid="rendy-backfill-durations-btn"
          >
            {backfilling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackageOpen className="h-4 w-4 mr-2" />}
            Backfill video durations
          </Button>
        </CardContent>
      </Card>
      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Rendy Settings
            {apiKeySet && <Badge className="bg-green-100 text-green-800 border-green-300 ml-2">Key configured</Badge>}
          </CardTitle>
          <CardDescription>
            Rendy turns property photos into AI marketing videos. Paste your organisation&apos;s API key below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>API key</Label>
              <Input
                type="password"
                placeholder="rpk_…"
                value={settings.api_key}
                onChange={(e) => setSettings((s) => ({ ...s, api_key: e.target.value }))}
                data-testid="rendy-api-key-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Base URL</Label>
              <Input
                placeholder="https://api.rendy.io/api/public/v1"
                value={settings.base_url}
                onChange={(e) => setSettings((s) => ({ ...s, base_url: e.target.value }))}
                data-testid="rendy-base-url-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveSettings} disabled={saving} data-testid="rendy-save-settings-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Endpoint explorer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-primary" /> Endpoint Explorer
          </CardTitle>
          <CardDescription>Fetch the live option lists Rendy exposes — presets, staging styles, staging animations and camera movements.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {[
            { key: "presets", label: "GET /presets" },
            { key: "staging_styles", label: "GET /staging/styles" },
            { key: "staging_animations", label: "GET /staging/animations" },
            { key: "camera_movements", label: "GET /camera-movements" },
          ].map(({ key, label }) => (
            <Button
              key={key}
              variant="outline"
              size="sm"
              onClick={() => runEndpoint(key)}
              disabled={busyAction !== null}
              data-testid={`rendy-endpoint-${key}`}
            >
              {busyAction === key ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              {label}
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Upload image */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" /> Upload Image
          </CardTitle>
          <CardDescription>POST /images/upload — host a local file with Rendy and get back a URL (optional; property gallery URLs already work directly in the step below).</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            data-testid="rendy-upload-file-input"
            className="text-sm"
          />
          <Button
            size="sm"
            onClick={uploadImage}
            disabled={busyAction !== null}
            data-testid="rendy-upload-btn"
          >
            {busyAction === "upload" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            Upload
          </Button>
        </CardContent>
      </Card>

      {/* Create sample video */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Film className="h-5 w-5 text-primary" /> Create Sample Video
          </CardTitle>
          <CardDescription>POST /listings → poll GET /listings/:id/status → GET /listings/:id. Pick a property (uses the field mapping above) or supply an address + image URLs manually.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useManual}
              onChange={(e) => setUseManual(e.target.checked)}
              data-testid="rendy-use-manual-toggle"
            />
            Use manual address / image URLs instead of a property
          </label>

          {!useManual ? (
            <div className="space-y-1">
              <Label>Property</Label>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full"
                data-testid="rendy-property-select"
              >
                {properties.map((p) => (
                  <option key={p.property_id} value={p.property_id}>
                    {p.title} — {p.property_id}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Address</Label>
                <Input
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  placeholder="123 Main St, Los Angeles, CA"
                  data-testid="rendy-manual-address-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Image URLs (one per line)</Label>
                <Textarea
                  value={manualImageUrls}
                  onChange={(e) => setManualImageUrls(e.target.value)}
                  placeholder="https://example.com/photo1.jpg"
                  rows={3}
                  data-testid="rendy-manual-images-input"
                />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Ratio</Label>
              <select
                value={ratio}
                onChange={(e) => setRatio(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full"
                data-testid="rendy-ratio-select"
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Preset key</Label>
              <Input value={presetKey} onChange={(e) => setPresetKey(e.target.value)} data-testid="rendy-preset-key-input" />
            </div>
            <div className="space-y-1">
              <Label>Camera action key (optional)</Label>
              <Input value={cameraActionKey} onChange={(e) => setCameraActionKey(e.target.value)} placeholder="e.g. SLIDER_LEFT" data-testid="rendy-camera-action-input" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={previewPayload} disabled={busyAction !== null} data-testid="rendy-preview-btn">
              {busyAction === "preview" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Preview payload
            </Button>
            <Button onClick={generateVideo} disabled={busyAction !== null} data-testid="rendy-generate-btn">
              {busyAction === "generate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Generate sample video
            </Button>
            {listingId && (
              <Button variant="outline" onClick={() => fetchDetail(listingId)} disabled={busyAction !== null} data-testid="rendy-refresh-detail-btn">
                {busyAction === "detail" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Get final video
              </Button>
            )}
          </div>

          {listingId && (
            <div className="text-xs text-muted-foreground" data-testid="rendy-listing-id-display">
              Listing ID: <code className="bg-muted px-1.5 py-0.5 rounded">{listingId}</code>
              {polling && <span className="ml-2 inline-flex items-center gap-1 text-blue-700"><Loader2 className="h-3 w-3 animate-spin" /> polling status…</span>}
            </div>
          )}

          {listingDetail?.videos?.length > 0 && (
            <div className="space-y-3 border-t pt-3" data-testid="rendy-video-preview">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Final render(s)</Label>
              {listingDetail.videos.map((v, i) => (
                <div key={v.url || i} className="space-y-1">
                  {v.url ? (
                    <video src={v.url} controls className="w-full max-w-md rounded border" data-testid={`rendy-video-${i}`} />
                  ) : (
                    <div className="text-xs text-amber-700">Video {i + 1} status: {v.status || "unknown"} ({v.progress ?? 0}%)</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageOpen className="h-5 w-5 text-primary" /> Bulk Export
          </CardTitle>
          <CardDescription>POST /listings/:id/export → poll GET /exports/:jobId, bundling all clips + final renders into a zip.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>Listing ID</Label>
            <Input value={listingId} onChange={(e) => setListingId(e.target.value)} className="w-64" data-testid="rendy-export-listing-input" />
          </div>
          <Button variant="outline" size="sm" onClick={startExport} disabled={busyAction !== null} data-testid="rendy-export-btn">
            {busyAction === "export" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
            Start export
          </Button>
          <div className="space-y-1">
            <Label>Job ID</Label>
            <Input value={jobId} onChange={(e) => setJobId(e.target.value)} className="w-64" data-testid="rendy-export-job-input" />
          </div>
          <Button variant="outline" size="sm" onClick={checkExport} disabled={busyAction !== null} data-testid="rendy-export-status-btn">
            {busyAction === "export_status" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            Check export status
          </Button>
        </CardContent>
      </Card>

      {/* Result viewer */}
      {lastResult && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              {lastResult.status === "dry-run" && <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-800">Dry-run</Badge>}
              {lastResult.status === "sent" && (
                <Badge className={lastResult.http_status >= 400 ? "bg-red-100 text-red-800 border-red-300" : "bg-green-100 text-green-800 border-green-300"}>
                  HTTP {lastResult.http_status}
                </Badge>
              )}
              {(lastResult.status === "error" || lastResult.status === "network-error") && (
                <Badge className="bg-red-100 text-red-800 border-red-300 flex items-center gap-1">
                  <X className="h-3 w-3" /> {lastResult.status}
                </Badge>
              )}
              {lastResult.endpoint && (
                <code className="text-xs bg-muted px-2 py-0.5 rounded truncate">{lastResult.endpoint}</code>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-end mb-1">
              <CopyButton text={JSON.stringify(lastResult, null, 2)} testId="rendy-test-response-copy" />
            </div>
            <pre className="p-3 text-xs bg-slate-950 text-slate-50 overflow-x-auto max-h-96 rounded" data-testid="rendy-test-response">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
